import { Component, h, Prop, Watch, Build } from '@stencil/core';
import { Event, EventEmitter, State, Element, Listen } from '@stencil/core';
import { debounce } from '../../../utils/helpers/debounce';
import { parseJsonProp } from '../../../utils/helpers/hydration';

/**
 * Language switcher dropdown component. Displays a list of available languages
 * and navigates to the selected language URL on selection.
 *
 * @product signnow
 * @source language
 * @version 1.0.0
 * @example
 *   <me-language-switcher
 *     items='[{"current": true, "link": "/en", "title": "English"}, {"current": false, "link": "/fr", "title": "Fran\u00e7ais"}]'
 *   ></me-language-switcher>
 */
@Component({
  tag: 'me-language-switcher',
  styleUrl: 'index.scss',
  shadow: false,
})
export class LanguageSwitcher {
  /**
   * An array of language options, where each object contains a language title, link, and current flag.
   * When a language is selected, the component will navigate to the specified link.
   * The current language is determined by the element with current: true.
   * Component will not render if less than 2 items are provided (runtime check only, not enforced in schema).
   *
   * @content
   * @dynamic
   * @type {array}
   * @items.type {object}
   * @items.property {boolean} current - Flag indicating if this is the currently active language
   * @items.property {string} link - The URL to navigate to when this language is selected
   * @items.property {string} title - The display name of the language (e.g., 'English', 'Українська', 'Français')
   * @items.required current
   * @items.required link
   * @items.required title
   * @items.content title
   */
  @Prop({ mutable: true }) items: any[] | string = [];

  /**
   * Event emitted when language is changed, before navigation occurs.
   * Can be used to track language changes or perform additional actions.
   */
  @Event({ eventName: 'languageChanged' }) languageChanged: EventEmitter<{ title: string; link: string; prev?: string }>;

  @State() private isOpen = false;
  @State() private selectedTitle?: string; // Locally stores the selected language title for immediate highlighting
  @State() private positionClass: 'language-switcher--top' | 'language-switcher--bottom' | '' = '';
  @State() private focusedIndex: number = -1;
  @Element() host!: HTMLElement;

  componentWillLoad() {
    this.items = parseJsonProp(this.items, []);
  }

  private labelRef?: HTMLDivElement;
  private listWrapRef?: HTMLDivElement;
  private resizeObserver?: ResizeObserver;
  private optionRefs: HTMLButtonElement[] = [];
  private debouncedCalculatePosition?: () => void;

  @Listen('click', { target: 'document' })
  onDocumentClick(e: MouseEvent) {
    if (!this.isOpen) return;
    const target = e.target as Node | null;
    if (target && !this.host.contains(target)) {
      this.isOpen = false;
    }
  }

  private toggleOpen = (e: MouseEvent | KeyboardEvent) => {
    // Only check target for mouse events
    if (e instanceof MouseEvent && e.target !== e.currentTarget) return;
    if (!this.isOpen) {
      // Calculate position before opening
      this.calculateDropdownPosition();
      this.isOpen = true;
    } else {
      this.isOpen = false;
    }
  };

  @Watch('isOpen')
  handleIsOpenChange() {
    if (this.isOpen) {
      // Clear inline styles to allow CSS to work
      if (this.listWrapRef) {
        this.listWrapRef.style.opacity = '';
        this.listWrapRef.style.visibility = '';
        this.listWrapRef.style.pointerEvents = '';
      }

      // Reset focused index only if it's not already set (preserve navigation state)
      if (this.focusedIndex < 0 && Array.isArray(this.items)) {
        const currentIndex = this.items.findIndex(item => item.current);
        this.focusedIndex = currentIndex >= 0 ? currentIndex : 0;
      }

      // Ensure position is calculated and applied before menu becomes visible
      requestAnimationFrame(() => {
        this.calculateDropdownPosition();
      });
      this.setupPositionTracking();
    } else {
      this.cleanupPositionTracking();
      // Reset position class and focused index when closed
      this.positionClass = '';
      this.focusedIndex = -1;
      // Force immediate hiding with inline styles
      if (this.listWrapRef) {
        this.listWrapRef.style.opacity = '0';
        this.listWrapRef.style.visibility = 'hidden';
        this.listWrapRef.style.pointerEvents = 'none';
      }
    }
  }

  private calculateDropdownPosition = () => {
    if (!Build.isBrowser || !this.labelRef || !this.listWrapRef) return;

    const labelRect = this.labelRef.getBoundingClientRect();
    const hostRect = this.host.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - labelRect.bottom;
    const spaceAbove = labelRect.top;

    const MAX_DROPDOWN_HEIGHT = 240;
    const PADDING = 4;
    const MIN_DROPDOWN_HEIGHT = 80;

    const availableBelow = Math.max(spaceBelow - PADDING, MIN_DROPDOWN_HEIGHT);
    const availableAbove = Math.max(spaceAbove - PADDING, MIN_DROPDOWN_HEIGHT);

    // Estimate dropdown height (approximate)
    const estimatedHeight = Math.min(this.items.length * 44 + 16, MAX_DROPDOWN_HEIGHT);
    const fitsBelow = estimatedHeight <= availableBelow;
    const fitsAbove = estimatedHeight <= availableAbove;
    const shouldShowBelow = fitsBelow || (!fitsAbove && availableBelow >= availableAbove);

    const maxHeight = Math.min(shouldShowBelow ? availableBelow : availableAbove, MAX_DROPDOWN_HEIGHT);
    this.listWrapRef.style.maxHeight = `${maxHeight}px`;

    // Calculate position relative to host element
    const topOffset = labelRect.bottom - hostRect.top;
    const bottomOffset = hostRect.bottom - labelRect.top;

    // Update position class and set CSS position
    const newPositionClass = shouldShowBelow ? 'language-switcher--bottom' : 'language-switcher--top';
    if (this.positionClass !== newPositionClass) {
      this.positionClass = newPositionClass;
    }

    // Set position via inline styles
    if (shouldShowBelow) {
      this.listWrapRef.style.top = `${topOffset}px`;
      this.listWrapRef.style.bottom = 'auto';
    } else {
      this.listWrapRef.style.top = 'auto';
      this.listWrapRef.style.bottom = `${bottomOffset}px`;
    }
  };

  private setupPositionTracking() {
    if (!Build.isBrowser) return;
    // Create debounced version of calculateDropdownPosition
    this.debouncedCalculatePosition = debounce(this.calculateDropdownPosition, 100);

    // Track window resize
    window.addEventListener('resize', this.debouncedCalculatePosition);
    window.addEventListener('scroll', this.debouncedCalculatePosition, true);

    // Track parent resize
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.debouncedCalculatePosition) {
          this.debouncedCalculatePosition();
        }
      });
      this.resizeObserver.observe(this.host);
    }
  }

  private cleanupPositionTracking() {
    if (!Build.isBrowser) return;
    if (this.debouncedCalculatePosition) {
      window.removeEventListener('resize', this.debouncedCalculatePosition);
      window.removeEventListener('scroll', this.debouncedCalculatePosition, true);
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }
  }

  /**
   * Handles language switching by navigating to the specified link.
   * Emits an event before navigation and immediately updates the UI state.
   */
  private switchLanguage(title: string, link: string) {
    const currentItem = Array.isArray(this.items) ? this.items.find(item => item.current) : undefined;
    const prev = currentItem ? currentItem.title : undefined;
    if (!title || !link || title === prev) {
      this.isOpen = false;
      return;
    }

    // Immediately update the selected language for instant UI feedback
    this.selectedTitle = title;

    // Force immediate hiding with inline styles before state change
    if (this.listWrapRef) {
      this.listWrapRef.style.opacity = '0';
      this.listWrapRef.style.visibility = 'hidden';
      this.listWrapRef.style.pointerEvents = 'none';
    }

    // Close the dropdown
    this.isOpen = false;
    this.focusedIndex = -1;
    // Don't reset positionClass to prevent visual jump

    // Emit event before navigation (parent can track the change)
    this.languageChanged.emit({ title, link, prev });

    // Navigate immediately (browser only)
    if (Build.isBrowser) {
      window.location.href = link;
    }
  }

  private scrollToFocusedOption = () => {
    if (this.focusedIndex >= 0 && this.optionRefs[this.focusedIndex] && this.listWrapRef) {
      requestAnimationFrame(() => {
        const focusedOption = this.optionRefs[this.focusedIndex];
        if (focusedOption) {
          // Scroll within the dropdown container, not the page
          const scrollTop = this.listWrapRef!.scrollTop;
          const optionOffsetTop = focusedOption.offsetTop;
          const optionHeight = focusedOption.offsetHeight;
          const containerHeight = this.listWrapRef!.clientHeight;

          // Calculate if option is outside visible area
          if (optionOffsetTop < scrollTop) {
            // Option is above visible area
            this.listWrapRef!.scrollTop = optionOffsetTop;
          } else if (optionOffsetTop + optionHeight > scrollTop + containerHeight) {
            // Option is below visible area
            this.listWrapRef!.scrollTop = optionOffsetTop + optionHeight - containerHeight;
          }
        }
      });
    }
  };

  private focusNextOption = () => {
    const currentIndex = this.focusedIndex;
    // If no item is selected (index -1), select first item (index 0)
    // If at last item, wrap to first item (circular navigation)
    // Otherwise move to next item
    if (currentIndex < 0) {
      this.focusedIndex = 0;
    } else if (currentIndex >= this.items.length - 1) {
      this.focusedIndex = 0; // Wrap to first item
    } else {
      this.focusedIndex = currentIndex + 1;
    }
    this.scrollToFocusedOption();
  };

  private focusPreviousOption = () => {
    const currentIndex = this.focusedIndex;
    // If no item is selected (index -1), select last item
    // If at first item (index 0), wrap to last item (circular navigation)
    // Otherwise move to previous item
    if (currentIndex < 0) {
      this.focusedIndex = this.items.length - 1;
    } else if (currentIndex === 0) {
      this.focusedIndex = this.items.length - 1; // Wrap to last item
    } else {
      this.focusedIndex = currentIndex - 1;
    }
    this.scrollToFocusedOption();
  };

  @Listen('keydown')
  handleKeyDown(event: KeyboardEvent) {
    const target = event.target as HTMLElement;
    const isLabel = target === this.labelRef || this.labelRef?.contains(target);
    const isOption = this.optionRefs.some(ref => ref === target || ref.contains(target));

    if (!isLabel && !isOption && !this.isOpen) {
      return;
    }

    switch (event.key) {
      case 'Enter':
      case ' ':
        if (isLabel && !this.isOpen) {
          event.preventDefault();
          // Calculate position before opening
          this.calculateDropdownPosition();
          this.isOpen = true;
        } else if (this.isOpen) {
          // Handle Enter when list is open
          // If event is on option button, let button's handler process it (it will stop propagation)
          // Otherwise, handle it here for keyboard navigation
          if (!isOption) {
            event.preventDefault();
            event.stopPropagation();

            // Use focusedIndex from keyboard navigation
            let indexToUse = -1;
            if (this.focusedIndex >= 0 && this.focusedIndex < this.items.length) {
              indexToUse = this.focusedIndex;
            } else {
              // Fallback: use first item if nothing is focused
              indexToUse = 0;
              this.focusedIndex = 0;
            }

            // Select the item
            const item = this.items[indexToUse];
            if (item) {
              this.switchLanguage(item.title, item.link);
            }
          }
          // If isOption, button's onKeyDown handler will process it with stopImmediatePropagation
        }
        break;

      case 'Escape':
        if (this.isOpen) {
          event.preventDefault();
          event.stopPropagation();
          this.isOpen = false;
          this.labelRef?.focus();
        }
        break;

      case 'ArrowDown':
        if (!this.isOpen) {
          event.preventDefault();
          // Calculate position before opening
          this.calculateDropdownPosition();
          this.isOpen = true;
        } else {
          event.preventDefault();
          this.focusNextOption();
        }
        break;

      case 'ArrowUp':
        if (!this.isOpen) {
          event.preventDefault();
          // Calculate position before opening
          this.calculateDropdownPosition();
          this.isOpen = true;
        } else {
          event.preventDefault();
          this.focusPreviousOption();
        }
        break;

      case 'Tab':
        if (this.isOpen) {
          // Close dropdown on Tab
          this.isOpen = false;
        }
        break;

      case 'Home':
        if (this.isOpen && this.items.length > 0) {
          event.preventDefault();
          this.focusedIndex = 0;
          this.scrollToFocusedOption();
        }
        break;

      case 'End':
        if (this.isOpen && this.items.length > 0) {
          event.preventDefault();
          this.focusedIndex = this.items.length - 1;
          this.scrollToFocusedOption();
        }
        break;
    }
  }

  render() {
    // Render only on client, skip on server (SSR)
    if (!Build.isBrowser) {
      return null;
    }

    // Component requires at least 2 language options to render
    if (!Array.isArray(this.items) || this.items.length < 2) {
      return null;
    }

    // Find the current language object in the items array
    const currentItem = this.items.find(item => item.current);

    // Active language: locally selected (after click) or current item if found in array
    // Only mark as active if the item exists in the items array
    const activeTitle = this.selectedTitle ?? (currentItem ? currentItem.title : undefined);

    // Label to display: selected title, or current item title from array
    const currentLabel = this.selectedTitle ?? (currentItem ? currentItem.title : '');

    return (
      <div class={`language-switcher ${this.isOpen ? 'is-open' : ''} ${this.positionClass}`}>
        <div
          ref={el => (this.labelRef = el)}
          class={`language-switcher__label ${activeTitle ? 'language-dropdown__option--active' : ''}`}
          onClick={this.toggleOpen}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              this.toggleOpen(e);
            }
          }}
          tabIndex={0}
          role="button"
          aria-expanded={this.isOpen}
          aria-haspopup="listbox"
        >
          {currentLabel}
        </div>
        <div ref={el => (this.listWrapRef = el)} class="language-dropdown__list-wrap">
          <ul class="language-dropdown__list" role="listbox">
            {this.items.map((item, index) => (
              <li class="language-dropdown__item" key={`${item.title}-${index}`} role="none">
                <button
                  ref={el => {
                    if (el) {
                      this.optionRefs[index] = el;
                    }
                  }}
                  type="button"
                  class={`language-dropdown__option js-language-switcher ${item.title === activeTitle ? 'language-dropdown__option--active' : ''} ${this.focusedIndex === index ? 'language-dropdown__option--focused' : ''}`}
                  data-language={item.title}
                  role="option"
                  aria-selected={item.title === activeTitle ? 'true' : 'false'}
                  tabIndex={this.isOpen && this.focusedIndex === index ? 0 : -1}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    // Update focusedIndex to match clicked item
                    this.focusedIndex = index;
                    this.switchLanguage(item.title, item.link);
                  }}
                  onKeyDown={e => {
                    // Handle Enter/Space on individual option buttons
                    // This is for direct interaction with button (mouse focus, tab navigation)
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      e.stopImmediatePropagation();
                      // Update focusedIndex to match this option
                      this.focusedIndex = index;
                      this.switchLanguage(item.title, item.link);
                    }
                  }}
                >
                  {item.title}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }
}
